import { Component, computed, input, ViewEncapsulation } from '@angular/core';
import {
    FormControl,
    FormsModule,
    ReactiveFormsModule,
    Validators,
} from '@angular/forms';
import { Icon } from '../icon/icon.component';
import { ControlErrors } from '../control-errors/control-errors.component';
import { notEmptyArray } from '../../validators/not-empty-array';

/**
 * Campo de formulário com rótulo, marcação de obrigatório e exibição de erros.
 *
 * Envolve um controle de formulário (input/select/textarea projetado via
 * `<ng-content>`), exibindo o {@link label}, um indicador de obrigatório quando
 * aplicável e as mensagens de validação ({@link errorMessages}).
 *
 * Usa `ViewEncapsulation.None` de propósito: estiliza o controle projetado por
 * meio de seletores descendentes (`.ai-field input`, etc.), o que não funciona
 * com encapsulamento `Emulated`. Além disso, a classe `.ai-field` é
 * reutilizada como layout de campo de leitura (`<div class="ai-field">`) por
 * telas da aplicação, portanto permanece global (classe compartilhada
 * documentada).
 */
@Component({
    selector: 'ai-field',
    standalone: true,
    encapsulation: ViewEncapsulation.None,
    imports: [FormsModule, ReactiveFormsModule, ControlErrors, Icon],
    templateUrl: './field.component.html',
    styleUrls: ['./field.component.scss'],
})
export class Field {
    /** Controle de formulário associado ao campo (obrigatório). */
    public readonly control = input.required<FormControl>();

    /** Texto do rótulo exibido acima do controle. */
    public readonly label = input<string>('');

    /** Força a exibição do indicador de obrigatório, mesmo sem validator. */
    public readonly forceRequired = input<boolean>(false);

    /** Mapa de mensagens de erro por chave de validação. */
    public readonly errorMessages = input<Record<string, string>>({
        required: 'Campo obrigatório',
    });

    /**
     * Indica se o campo é obrigatório, derivado dos validators do
     * {@link control} (`required` ou `notEmptyArray`).
     */
    public readonly required = computed<boolean>(
        () =>
            this.control().hasValidator(Validators.required) ||
            this.control().hasValidator(notEmptyArray),
    );
}
